import { constants as coreConstants, coreActions, Promise, services } from '@ambita/ambita-components-core';
import * as riot from 'riot';
import * as constants from './constants/common';
import { handleError } from './errorHandler';
import * as cadastreReportService from './services/cadastreReport';
import { QueryParams } from './services/queryParams';
import { setCSVData, setReadyState } from './services/integration';
import { fetchConfig } from './services/config';
import './views/neighborList/neighborListReport.tag';
import './views/ownerList/ownerListReport.tag';
import './views/landRegistry/landRegistryReport.tag';
import './views/cadastreReport/cadastreReportWrapper.tag';
import './views/cadastreReport/cadastreReportCadastreInfoWrapper.tag';
import './views/cadastreReport/cadastreReportBuildingsWrapper.tag';
import './views/multipleReport/multipleReport.tag';
import './views/brokerLetterReport/brokerLetterReport.tag';
import { getPropertyKeyFromOrderData } from './services/common';
import * as landRegistryLookupService from './services/landRegistryLookup';
import * as neighborListService from './services/neighborList';
import * as ownerListService from './services/ownerList';
import { NeighborListReport } from './typings/NeighborList';

const REPORT_CONTAINER = '#report-container';

const createReportDate = (): string => {
  return services.filters.date(new Date(), 'dd.MM.yyyy');
};

const setTokenExpiryHandler = (): void => {
  services.broadcast.subscribe((message: string) => {
    if (message === coreConstants.broadcast.TOKEN_EXPIRED_MESSAGE) {
      handleError('Token has expired');
    }
  });
};

const mountNeighborListReport = (params: QueryParams, reportData: NeighborListReport) => {
  if (params.makeCSV) {
    setCSVData(neighborListService.generateCSV(reportData));
  }

  riot.mount(REPORT_CONTAINER, 'nbl-report', {
    date: createReportDate(),
    reportData,
    noMap: params.noMap,
    mapTask: {
      type: constants.TASK_NEIGHBOR_LIST,
      geojson: reportData.orderData.geojson,
      onTaskDone: setReadyState
    }
  });
};

const createNeighborListReport = (params: QueryParams): Promise<any> => {
  return neighborListService.fetchReportData(params.orderId, params.product).then(reportData => {
    mountNeighborListReport(params, reportData);
  });
};

const createNeighborListReportWithCadastreInput = (params: QueryParams): Promise<any> => {
  return neighborListService.fetchReportDataForCadastreInput(params.orderId, params.product).then(reportData => {
    mountNeighborListReport(params, reportData);
  });
};

const createOwnerListReport = (params: QueryParams): Promise<any> => {
  return ownerListService.fetchReportData(params.orderId).then(reportData => {
    if (params.makeCSV) {
      setCSVData(ownerListService.generateCSV(reportData));
    }

    riot.mount(REPORT_CONTAINER, 'ownerlist-report', {
      date: createReportDate(),
      mapTask: {
        type: constants.TASK_OWNER_LIST,
        geojson: reportData.geojson,
        onTaskDone: setReadyState
      }
    });
  });
};

const createLandRegistryLookupReport = (params: QueryParams): Promise<any> => {
  return landRegistryLookupService.fetchReportData(params.orderId, params.product).then(reportData => {
    setTitle(reportData, reportData.componentsToMount.reportTitle);

    riot.mount(REPORT_CONTAINER, 'landregistry-report', {
      componentsToMount: reportData.componentsToMount,
      propertyKey: getPropertyKeyFromOrderData(reportData.orderData),
      onTaskDone: setReadyState
    });
  });
};

const createCadastreReport = (params: QueryParams): Promise<any> => {
  return cadastreReportService.fetchReportData(params.orderId, params.product).then(reportData => {
    setTitle(reportData, 'Matrikkelrapport');

    riot.mount(REPORT_CONTAINER, 'ambita-cadastre-report-wrapper', {
      date: createReportDate(),
      propertyKey: getPropertyKeyFromOrderData(reportData.orderData),
      onTaskDone: setReadyState
    });
  });
};

const createCadastreReportInfoPart = (params: QueryParams): Promise<any> => {
  return cadastreReportService.fetchReportData(params.orderId, params.product).then(reportData => {
    setTitle(reportData, 'Matrikkelrapport');

    riot.mount(REPORT_CONTAINER, 'ambita-cadastre-report-cadastre-info-wrapper', {
      date: createReportDate(),
      propertyKey: getPropertyKeyFromOrderData(reportData.orderData),
      onTaskDone: setReadyState
    });
  });
};

const createCadastreReportBuildingsPart = (params: QueryParams): Promise<any> => {
  return cadastreReportService.fetchReportData(params.orderId, params.product).then(reportData => {
    setTitle(reportData, 'Matrikkelrapport');

    riot.mount(REPORT_CONTAINER, 'ambita-cadastre-report-buildings-wrapper', {
      date: createReportDate(),
      propertyKey: getPropertyKeyFromOrderData(reportData.orderData),
      onTaskDone: setReadyState
    });
  });
};

const createMultipleReport = (params: QueryParams): Promise<any> => {
  return cadastreReportService.fetchReportData(params.orderId, params.product).then(reportData => {
    const { orderData } = reportData;

    if (!orderData) {
      return Promise.reject(Error(`Order ${params.orderId} has no product ${params.product}`));
    }
    setTitle(reportData);

    const propertyKey = getPropertyKeyFromOrderData(orderData);

    if (!propertyKey) {
      return Promise.reject(Error(`Cannot generate report for product ${params.product}, invalid property identifier`));
    }

    const landRegistryComponentsToMount = landRegistryLookupService.getLandRegistryComponentsToMount(orderData);
    const cadastreReportComponentsToMount = cadastreReportService.getCadastreReportsToMount(orderData);

    riot.mount(REPORT_CONTAINER, 'ambita-multiple-report', {
      landRegistryComponentsToMount,
      cadastreReportComponentsToMount,
      propertyKey,
      products: orderData.products,
      onTaskDone: setReadyState
    });
  });
};

const createBrokerLetterReport = (params: QueryParams): Promise<any> => {
  return cadastreReportService.fetchReportData(params.orderId, params.product).then(reportData => {
    const { orderData } = reportData;

    if (!orderData) {
      return Promise.reject(Error(`Order ${params.orderId} has no product ${params.product}`));
    }

    const propertyKey = getPropertyKeyFromOrderData(orderData);

    if (!propertyKey) {
      return Promise.reject(Error(`Cannot generate report for product ${params.product}, invalid property identifier`));
    }

    riot.mount(REPORT_CONTAINER, 'ambita-broker-letter-report', {
      propertyKey,
      onTaskDone: setReadyState
    });
  });
};

export const initializeGenerator = (params: QueryParams): Promise<void> => {
  const storedAccessToken = sessionStorage.getItem('access_token');

  if (!params.token && !storedAccessToken) {
    return Promise.reject(Error('No valid token provided'));
  }

  services.i18n.setLocale('nb');
  setTokenExpiryHandler();

  return fetchConfig()
    .then(config => {
      coreActions.changeSettings({
        environment: config.environment.toLowerCase(),
        persistGeoTokenInStorage: false,
        persistAccessTokenInStorage: false,
        tokenObject: {
          token_type: 'Bearer',
          access_token: params.token ? params.token : storedAccessToken,
          refresh_token: '',
          expires_in: 3600,
          scope: []
        }
      });
    });
};

export const generateReport = (params: QueryParams): Promise<any> => {
  if (neighborListService.isNeighborListProduct(params.product)) {
    return createNeighborListReport(params);
  } else if (neighborListService.isNeighborListWithCadastreInput(params.product)) {
    return createNeighborListReportWithCadastreInput(params);
  } else if (params.product === constants.PRODUCT_OWNER_LIST || params.product === constants.PRODUCT_OWNER_LIST_NEW) {
    return createOwnerListReport(params);
  } else if (landRegistryLookupService.isLandRegistryLookupProduct(params.product)) {
    return createLandRegistryLookupReport(params);
  } else if (params.product === constants.PRODUCT_CADASTRE_REPORT) {
    return createCadastreReport(params);
  } else if (params.product === constants.PRODUCT_CADASTRE_REPORT_INFO) {
    return createCadastreReportInfoPart(params);
  } else if (params.product === constants.PRODUCT_CADASTRE_REPORT_BUILDINGS) {
    return createCadastreReportBuildingsPart(params);
  } else if (params.product === constants.PRODUCT_MULTIPLE_REPORT_CADASTRE) {
    return createMultipleReport(params);
  } else if (params.product === constants.PRODUCT_BROKER_LETTER_REPORT) {
    return createBrokerLetterReport(params);
  } else {
    return Promise.reject(Error(`Cannot generate report for product ${params.product}`));
  }
};

const setTitle = (reportData, reportTitle = 'Rapport'): void => {
  if (!reportData.orderData) {
    return;
  }

  const {
    realty: {
      cadastre: {
        knr, gnr, bnr, fnr, snr
      },
      address: {
        adresse
      }
    }
  } = reportData.orderData;

  const propertyWithNoAdress = adresse !== 'Eiendommen har ikke registrert adresse';
  const property: string = propertyWithNoAdress ? `${adresse}` : `${knr}-${gnr}-${bnr}-${fnr}-${snr}`;

  document.title = `${reportTitle} - ${property} | Infoland`;
};
